import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Users, TrendingUp, Download, FileText, Loader2, AlertCircle, Award } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAdminAnalytics } from "@/hooks/use-admin-analytics";
import { getLocalizedErrorMessage } from "@/lib/error-i18n";

export default function AdminAnalytics() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const { toast } = useToast();
  const [timeRange, setTimeRange] = useState('month');

  const { data, isLoading, isError, error } = useAdminAnalytics();

  const exportCSV = () => {
    if (!data) return;
    
    try {
      // Create CSV content
      const headers = [t("admin.metric"), t("admin.value")];
      const rows = [
        [t("admin.totalUsers"), data.totalUsers.toString()],
        [t("admin.activeUsers"), data.activeUsers.toString()],
        [t("admin.completionRate"), `${data.completionRate}%`],
        [t("admin.avgProgress"), `${data.avgProgress}%`],
        ['', ''],
        [t("admin.topStrengths"), ''],
        [t("admin.name"), t("admin.arabicName"), t("admin.domain"), t("admin.userCount"), t("admin.avgRank")],
        ...data.popularStrengths.map(s => [s.name, s.nameAr, s.domain, s.userCount.toString(), s.avgRank.toFixed(1)])
      ];
      
      const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast({
        title: t('admin.exportSuccess'),
        description: t('admin.exportSuccessDesc'),
      });
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('admin.exportFailedDesc'),
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <Card className="p-4 bg-card border-border sm:p-8">
        <div className="text-center space-y-4">
          <AlertCircle className="w-12 h-12 text-destructive mx-auto" />
          <h3 className="text-xl font-semibold">{t('common.error')}</h3>
          <p className="text-muted-foreground">
            {getLocalizedErrorMessage(error, "admin.analyticsLoadFailed")}
          </p>
        </div>
      </Card>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-6" dir={isRtl ? "rtl" : "ltr"}>
      <Card className="p-4 bg-card border-border sm:p-6">
        <div className="flex flex-col gap-3 mb-6 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-xl font-bold sm:text-2xl">{t('admin.analytics')}</h2>
          <Button onClick={exportCSV} variant="outline" className="w-full gap-2 sm:w-auto">
            <Download className="w-4 h-4" />
            {t('admin.exportCSV')}
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-2 lg:grid-cols-4">
          <Card className="p-4">
            <Users className="w-8 h-8 text-primary mb-2" />
            <div className="break-words text-2xl font-bold">{data.totalUsers}</div>
            <p className="text-sm text-muted-foreground">{t('admin.totalUsers')}</p>
          </Card>
          <Card className="p-4">
            <Users className="w-8 h-8 text-accent mb-2" />
            <div className="break-words text-2xl font-bold">{data.activeUsers}</div>
            <p className="text-sm text-muted-foreground">{t('admin.activeUsers')}</p>
          </Card>
          <Card className="p-4">
            <TrendingUp className="w-8 h-8 text-green-500 mb-2" />
            <div className="break-words text-2xl font-bold">{data.completionRate}%</div>
            <p className="text-sm text-muted-foreground">{t('admin.completionRate')}</p>
          </Card>
          <Card className="p-4">
            <FileText className="w-8 h-8 text-blue-500 mb-2" />
            <div className="break-words text-2xl font-bold">{data.avgProgress}%</div>
            <p className="text-sm text-muted-foreground">{t('admin.avgProgress')}</p>
          </Card>
        </div>

        <h3 className="text-lg font-semibold mb-4">{t('admin.topStrengths')}</h3>
        <div className="space-y-2">
          {data.popularStrengths.map((strength, idx) => (
            <div key={idx} className="flex flex-col gap-3 p-4 bg-muted/50 rounded-lg border border-border/50 hover:bg-muted/70 transition-colors sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 flex-col gap-1">
                <span className="break-words text-base font-semibold">{strength.name}</span>
                <span className="break-words text-sm text-muted-foreground">{strength.nameAr}</span>
                <span className="break-words text-xs text-muted-foreground/70">{strength.domain}</span>
              </div>
              <div className="flex flex-col items-start gap-1 sm:items-end">
                <Badge variant="secondary" className="gap-1.5 px-3 py-1.5 bg-primary/10 hover:bg-primary/20 text-primary border-primary/20">
                  <Award className="w-3.5 h-3.5" />
                  <span className="font-semibold">{strength.avgRank.toFixed(1)}</span>
                </Badge>
                <span className="text-xs text-muted-foreground">{t('admin.avgRank')}</span>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
